/**
 * One space's page, pure parts: which tab the URL names, the line under the
 * space's name, the client organization's program as the page lists it
 * (documents, findings, record updates, the stat tiles) and, for every row,
 * whether the client's people are shown it under the space's client access
 * settings. The caller passes data already scoped to the client organization
 * (its own reads) and chats already scoped to the host (`selectOrgThreads`);
 * nothing here widens either.
 *
 * The badges only describe the settings. The server applies them when a
 * guest reads the program; this page never decides what a guest receives.
 *
 * @module components/spaces/SpacePage.logic
 */
import { isRecordUpdateKind } from "@repo/shared/findingPlanning";
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";
import { SPACE_DOCUMENT_ID_PATTERN } from "@amoreg/contracts";

import type { Recommendation } from "../../state/governance";
import type { Policy } from "../../state/policies";
import type { ClientAccess, Space, SpaceDocumentSummary, SpaceDocumentVersion } from "../../state/spaces";
import { compactAge, threadActivityMs } from "../home/homeLogic";
import type { FindingItem } from "../regulation/finding/findingItem";
import { recordStatusOf } from "../regulation/recordUpdates/recordUpdates.logic";

/* ------------------------------------------------------------------ */
/* The page's URL                                                      */
/* ------------------------------------------------------------------ */

/**
 * A space is one overview page. Its URL can open one of the space's
 * documents in place (`?doc=board`) or the settings over the page
 * (`?settings=1`); the bare URL is the overview.
 */
export interface SpacePageView {
  /** A well-formed space document id open in place, or null for the overview. */
  readonly doc: string | null;
  /** Settings (context, who has access) open over the page. */
  readonly settings: boolean;
}

export interface SpaceSearch {
  readonly doc?: string;
  readonly settings?: 1;
}

/**
 * The view a search names. Links from earlier layouts still land: the one
 * scroll's `?section=board` and the tabs' `?tab=files&doc=board` open the
 * Board, and `?tab=settings` opens the settings.
 */
export function parseSpaceView(search: {
  readonly doc?: unknown;
  readonly settings?: unknown;
  readonly tab?: unknown;
  readonly section?: unknown;
}): SpacePageView {
  const doc =
    typeof search.doc === "string" && SPACE_DOCUMENT_ID_PATTERN.test(search.doc)
      ? search.doc
      : search.section === "board"
        ? "board"
        : null;
  const settings =
    search.settings === 1 || search.settings === "1" || search.settings === true || search.tab === "settings";
  return { doc, settings };
}

/** The route's search for a view: the overview is the bare URL. */
export function spaceViewSearch(view: SpacePageView): SpaceSearch {
  return {
    ...(view.doc !== null ? { doc: view.doc } : {}),
    ...(view.settings ? { settings: 1 as const } : {}),
  };
}

/** How many chats the overview lists before "See all". */
export const OVERVIEW_CHATS = 6;

/* ------------------------------------------------------------------ */
/* Names                                                               */
/* ------------------------------------------------------------------ */

/**
 * The host's name where a badge must stay short: "Apex Fund Services" reads
 * "Apex only" as the mockup has it. A name of up to 16 characters stays whole.
 */
export function hostShortName(name: string | null | undefined): string {
  const trimmed = name?.trim() ?? "";
  if (trimmed.length === 0) return "Host";
  if (trimmed.length <= 16) return trimmed;
  return trimmed.split(/\s+/)[0] ?? trimmed;
}

/** The client's name, or a neutral phrase when the directory did not answer. */
export function clientName(space: Pick<Space, "client">): string {
  return space.client?.name?.trim() || "the client";
}

/**
 * The line under the space's name. "Client space · managed by Apex Fund
 * Services · client: Whitestar Fund", or "Space · Apex Fund Services".
 */
export function spaceSubtitle(input: {
  readonly space: Pick<Space, "client">;
  readonly hostName: string | null;
}): string {
  const host = input.hostName?.trim() || null;
  if (input.space.client === null) return host ? `Space · ${host}` : "Space";
  return [
    "Client space",
    host ? `managed by ${host}` : null,
    `client: ${input.space.client.name?.trim() || "linked organization"}`,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

/* ------------------------------------------------------------------ */
/* What the client is shown                                            */
/* ------------------------------------------------------------------ */

export interface Visibility {
  /** The settings show it to the client's people. */
  readonly shown: boolean;
  /** "Client sees" or "Apex only". */
  readonly label: string;
  /** One sentence for the badge's title. */
  readonly detail: string;
}

export interface VisibilityNames {
  /** Short host name ("Apex"). */
  readonly host: string;
  /** Client name ("Whitestar Fund"). */
  readonly client: string;
}

export type ProgramDocumentKind = "policy" | "procedure";

const shown = (detail: string): Visibility => ({ shown: true, label: "Client sees", detail });
const withheld = (names: VisibilityNames, detail: string): Visibility => ({
  shown: false,
  label: `${names.host} only`,
  detail,
});

const DOCUMENT_KEY: Readonly<Record<ProgramDocumentKind, "policies" | "procedures">> = {
  policy: "policies",
  procedure: "procedures",
};

/** A policy or a procedure: its kind's key, with a word on its history. */
export function documentVisibility(
  access: ClientAccess,
  kind: ProgramDocumentKind,
  names: VisibilityNames,
): Visibility {
  const noun = kind === "policy" ? "policies" : "procedures";
  if (!access[DOCUMENT_KEY[kind]]) {
    return withheld(names, `${names.client} is not shown ${noun}.`);
  }
  return shown(
    access.versions
      ? `${names.client} sees this document, its versions and its change history.`
      : `${names.client} sees the current version only.`,
  );
}

/**
 * A finding: findings must be on, and its document must be shown. A finding
 * with no document (a missing document) follows the findings key alone.
 */
export function findingVisibility(
  access: ClientAccess,
  documentKind: ProgramDocumentKind | null,
  names: VisibilityNames,
): Visibility {
  if (!access.findings) return withheld(names, `${names.client} is not shown findings.`);
  if (documentKind !== null && !access[DOCUMENT_KEY[documentKind]]) {
    return withheld(names, `${names.client} is not shown the document this finding is on.`);
  }
  return shown(
    access.findingBasis
      ? `${names.client} sees this finding and its regulatory basis.`
      : `${names.client} sees this finding without its regulatory basis.`,
  );
}

/** Record updates as one group: applied updates on shown documents. */
export function recordUpdatesVisibility(access: ClientAccess, names: VisibilityNames): Visibility {
  return access.recordUpdates
    ? shown(`${names.client} sees applied suggestions on the documents it is shown.`)
    : withheld(names, `${names.client} is not shown suggestions.`);
}

/* ------------------------------------------------------------------ */
/* The program                                                         */
/* ------------------------------------------------------------------ */

const timeMs = (iso: string | null | undefined): number => {
  const parsed = iso ? Date.parse(iso) : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

export interface ProgramDocument {
  readonly id: string;
  readonly name: string;
  readonly kind: ProgramDocumentKind;
  /** The version in force. */
  readonly version: number;
  /** When the version in force arrived; 0 when unknown. */
  readonly updatedAtMs: number;
}

/** The library fields the program reads; a `Policy` is one. */
export interface ProgramPolicy {
  readonly id: string;
  readonly filename: string;
  readonly title?: string | null | undefined;
  readonly version?: number | undefined;
  readonly versionUploadedAt?: string | undefined;
  readonly documentKind?: Policy["documentKind"] | undefined;
  readonly createdAt: string;
  readonly reference?: boolean | undefined;
}

/**
 * The client's policies and procedures, most recently updated first.
 * Frameworks and reference material (the agent's knowledge) are not the
 * program's documents and are left out.
 */
export function programDocuments<P extends ProgramPolicy>(
  policies: ReadonlyArray<P>,
  displayName: (policy: P) => string,
): ProgramDocument[] {
  return policies
    .flatMap((policy): ProgramDocument[] => {
      const kind = policy.documentKind ?? "policy";
      if ((kind !== "policy" && kind !== "procedure") || policy.reference === true) return [];
      return [
        {
          id: policy.id,
          name: displayName(policy),
          kind,
          version: policy.version ?? 1,
          updatedAtMs: timeMs(policy.versionUploadedAt) || timeMs(policy.createdAt),
        },
      ];
    })
    .toSorted((left, right) => right.updatedAtMs - left.updatedAtMs || left.name.localeCompare(right.name));
}

/** When a finding last moved: its decision, else when it was raised. */
export const findingMovedMs = (item: Pick<FindingItem, "raisedAt" | "decision">): number =>
  Math.max(item.raisedAt, timeMs(item.decision?.at));

export interface FindingsSummary {
  readonly open: number;
  readonly resolved: number;
  /** Open first, then the rest; most recently moved first within each. Dismissed ones are left out. */
  readonly latest: ReadonlyArray<FindingItem>;
}

export function summarizeFindings(items: ReadonlyArray<FindingItem>, limit: number): FindingsSummary {
  let open = 0;
  let resolved = 0;
  for (const item of items) {
    if (item.status === "open") open += 1;
    else if (item.status === "resolved") resolved += 1;
  }
  const latest = items
    .filter((item) => item.status !== "dismissed")
    .toSorted(
      (left, right) =>
        Number(right.status === "open") - Number(left.status === "open") ||
        findingMovedMs(right) - findingMovedMs(left) ||
        left.id.localeCompare(right.id),
    )
    .slice(0, Math.max(0, limit));
  return { open, resolved, latest };
}

export interface RecordUpdateCounts {
  readonly pending: number;
  readonly applied: number;
}

/** Housekeeping suggestions only; gap suggestions are findings. */
export function recordUpdateCounts(
  recommendations: ReadonlyArray<Pick<Recommendation, "kind" | "status">>,
): RecordUpdateCounts {
  let pending = 0;
  let applied = 0;
  for (const suggestion of recommendations) {
    if (!isRecordUpdateKind(suggestion.kind)) continue;
    const status = recordStatusOf(suggestion.status);
    if (status === "pending") pending += 1;
    else if (status === "applied") applied += 1;
  }
  return { pending, applied };
}

export interface ProgramStats {
  readonly documents: number;
  readonly openFindings: number;
  readonly resolvedFindings: number;
  /** The latest document version or finding move; null when there is none. */
  readonly lastUpdateMs: number | null;
}

export function programStats(
  documents: ReadonlyArray<Pick<ProgramDocument, "updatedAtMs">>,
  findings: ReadonlyArray<FindingItem>,
): ProgramStats {
  let last = 0;
  for (const document of documents) last = Math.max(last, document.updatedAtMs);
  let openFindings = 0;
  let resolvedFindings = 0;
  for (const item of findings) {
    if (item.status === "open") openFindings += 1;
    else if (item.status === "resolved") resolvedFindings += 1;
    last = Math.max(last, findingMovedMs(item));
  }
  return { documents: documents.length, openFindings, resolvedFindings, lastUpdateMs: last > 0 ? last : null };
}

/** "2d ago", "just now"; "—" for unknown. */
export function ageLabel(ms: number | null, now: number): string {
  if (ms === null || ms <= 0) return "—";
  const age = compactAge(new Date(ms).toISOString(), now);
  return age === "now" ? "just now" : `${age} ago`;
}

/** The stat tile's figure: "2d", "now", "—". */
export function ageFigure(ms: number | null, now: number): string {
  if (ms === null || ms <= 0) return "—";
  return compactAge(new Date(ms).toISOString(), now);
}

/* ------------------------------------------------------------------ */
/* Chats                                                               */
/* ------------------------------------------------------------------ */

/** The space's chats, latest activity first. */
export function spaceChats<T extends Pick<EnvironmentThreadShell, "id" | "updatedAt" | "latestUserMessageAt">>(
  threads: ReadonlyArray<T>,
  spaceId: string,
  spaceIdByThreadId: ReadonlyMap<string, string>,
): T[] {
  return threads
    .filter((thread) => spaceIdByThreadId.get(thread.id) === spaceId)
    .toSorted((left, right) => threadActivityMs(right) - threadActivityMs(left));
}

/* ------------------------------------------------------------------ */
/* Files and people                                                    */
/* ------------------------------------------------------------------ */

/** One file made or uploaded in one of the space's chats. */
export interface SpaceChatFile {
  readonly threadId: string;
  readonly threadTitle: string;
  readonly path: string;
  readonly name: string;
  readonly size: number;
  readonly modifiedAt: string;
}

/**
 * The files of the space's chats, newest first, from the person's workspace
 * catalog (which lists only conversations the person may open). Chats in
 * other spaces, or in none, are left out.
 */
export function spaceChatFiles(
  threads: ReadonlyArray<{
    readonly threadId: string;
    readonly title: string;
    readonly files: ReadonlyArray<{ readonly path: string; readonly size: number; readonly modifiedAt: string }>;
  }>,
  spaceThreadIds: ReadonlySet<string>,
): SpaceChatFile[] {
  return threads
    .filter((thread) => spaceThreadIds.has(thread.threadId))
    .flatMap((thread) =>
      thread.files.map((file) => ({
        threadId: thread.threadId,
        threadTitle: thread.title.trim() || "Untitled chat",
        path: file.path,
        name: file.path.split("/").pop() || file.path,
        size: file.size,
        modifiedAt: file.modifiedAt,
      })),
    )
    .toSorted((left, right) => Date.parse(right.modifiedAt) - Date.parse(left.modifiedAt));
}

/** A person on the space's team, as Settings lists them. */
export interface SpaceMember {
  readonly id: string;
  readonly email: string;
  readonly name: string | null;
  /** The person's role in the host organization. */
  readonly role: "admin" | "member";
  readonly avatarUrl: string | null;
}

/**
 * Who works in the space: the host organization's members, and for a
 * client space only those who are also members of the client organization
 * (the ethical wall, `spaceAccess.ts`). Admins first, then by name.
 */
export function spaceMembers(
  hostTeam: ReadonlyArray<{
    readonly id: string;
    readonly email: string;
    readonly name: string | null;
    readonly role: string;
    readonly avatarUrl?: string | null;
  }>,
  clientTeam: ReadonlyArray<{ readonly id: string; readonly email: string }> | null,
): SpaceMember[] {
  const inClient =
    clientTeam === null ? null : new Set(clientTeam.map((member) => member.email.trim().toLowerCase()));
  return hostTeam
    .filter((member) => inClient === null || inClient.has(member.email.trim().toLowerCase()))
    .map((member) => ({
      id: member.id,
      email: member.email,
      name: member.name,
      role: member.role === "admin" || member.role === "owner" ? ("admin" as const) : ("member" as const),
      avatarUrl: member.avatarUrl ?? null,
    }))
    .toSorted(
      (left, right) =>
        (left.role === right.role ? 0 : left.role === "admin" ? -1 : 1) ||
        (left.name ?? left.email).localeCompare(right.name ?? right.email),
    );
}

/** A file size for a row: "812 B", "14 KB", "2.3 MB". */
export function fileSizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A row of the overview's Documents: the Board first, always, then the rest by their last edit. */
export interface SpaceDocumentRow {
  readonly docId: string;
  readonly kind: string;
  readonly name: string;
  readonly summary: string;
  /** Null for the Board before anyone has opened it. */
  readonly updatedAt: string | null;
  readonly versions: ReadonlyArray<SpaceDocumentVersion>;
}

const BOARD_SUMMARY = "The space's live page: status, deliveries and notes";

/** "Spreadsheet · uploaded, 14 KB", "Markdown page · uploaded, 2 KB", or the bare kind. */
function documentSummary(doc: SpaceDocumentSummary): string {
  const kind = doc.kind === "sheet" ? (doc.file ? "Spreadsheet" : "Sheet") : doc.file ? "Markdown page" : "Page";
  return doc.file ? `${kind} · uploaded, ${fileSizeLabel(doc.file.size)}` : kind;
}

export function spaceDocumentRows(documents: ReadonlyArray<SpaceDocumentSummary>): SpaceDocumentRow[] {
  const board = documents.find((doc) => doc.docId === "board") ?? null;
  const rest = documents
    .filter((doc) => doc.docId !== "board")
    .toSorted((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt));
  const shared = (doc: SpaceDocumentSummary) =>
    doc.audience === "client_edit" ? " · the client can edit" : doc.audience === "client_view" ? " · the client can view" : "";
  return [
    {
      docId: "board",
      kind: "board",
      name: board?.title?.trim() || "Board",
      summary: board ? `${BOARD_SUMMARY}${shared(board)}` : BOARD_SUMMARY,
      updatedAt: board?.updatedAt ?? null,
      versions: board?.versions ?? [],
    },
    ...rest.map((doc) => ({
      docId: doc.docId,
      kind: doc.kind,
      name: doc.title?.trim() || (doc.kind === "sheet" ? "Untitled sheet" : "Untitled page"),
      summary: `${documentSummary(doc)}${shared(doc)}`,
      updatedAt: doc.updatedAt,
      versions: doc.versions,
    })),
  ];
}
